// ========================================
// HELPERS.JS - Fonctions Utilitaires
// Avec support du filtrage Zavatsoa
// ========================================

import { ROLES } from './constants';
import { logger, performance, logFirebaseError } from './logger';

// Import statique des librairies d'export
// IMPORTANT: jspdf-autotable doit être importé APRÈS jsPDF pour ajouter autoTable au prototype
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable'; // Import nommé pour autotable


// ========================================
// FORMATAGE DES DATES
// ========================================

export const formatDateShort = (date) => {
  if (!date) return '-';

  try {
    const dateObj = date.toDate ? date.toDate() : new Date(date);

    if (isNaN(dateObj.getTime())) return '-';

    return new Intl.DateTimeFormat('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    }).format(dateObj);
  } catch (error) {
    return '-';
  }
};

// ========================================
// 1. GESTION DES PERMISSIONS
// ========================================

/**
 * Vérifie si un utilisateur a une permission spécifique
 * @param {Object} user - L'utilisateur à vérifier
 * @param {string} permission - La permission à vérifier
 * @returns {boolean}
 */
export const hasPermission = (user, permission) => {
  if (!user) return false;
  
  // Les admins ont toutes les permissions
  if (user.role === 'admin') return true;
  
  // Vérifier si l'utilisateur a la permission 'all'
  if (user.permissions?.includes('all')) return true;
  
  // Récupérer le rôle de l'utilisateur
  const role = ROLES[user.role];
  if (!role) return false;
  
  // Vérifier la permission spécifique
  if (role.permissions?.includes(permission)) return true;
  
  // Gérer les permissions wildcard
  // view_all donne accès à toutes les permissions view_*
  if (permission.startsWith('view_') && role.permissions?.includes('view_all')) {
    return true;
  }
  
  // edit_all donne accès à toutes les permissions edit_*
  if (permission.startsWith('edit_') && role.permissions?.includes('edit_all')) {
    return true;
  }
  
  // delete_all donne accès à toutes les permissions delete_*
  if (permission.startsWith('delete_') && role.permissions?.includes('delete_all')) {
    return true;
  }
  
  // manage_all donne accès à toutes les permissions manage_*
  if (permission.startsWith('manage_') && role.permissions?.includes('manage_all')) {
    return true;
  }
  
  // export_all donne accès à toutes les permissions export_*
  if (permission.startsWith('export_') && role.permissions?.includes('export_all')) {
    return true;
  }
  
  return false;
};

/**
 * Vérifie si un utilisateur peut voir les transactions Zavatsoa
 * @param {Object} user - L'utilisateur à vérifier
 * @returns {boolean}
 */
export const canViewZavatsoa = (user) => {
  if (!user) return false;
  
  // Rôles autorisés à voir Zavatsoa
  const allowedRoles = ['admin', 'tresorier', 'president', 'pasteur'];
  return allowedRoles.includes(user.role);
};

/**
 * Filtre les transactions selon les permissions de l'utilisateur
 * Cache les transactions Zavatsoa pour les utilisateurs non autorisés
 * @param {Array} transactions - Liste des transactions
 * @param {Object} user - L'utilisateur actuel
 * @returns {Array} - Transactions filtrées
 */
export const filterTransactionsByPermission = (transactions, user) => {
  if (!transactions || !Array.isArray(transactions)) return [];
  
  // Si l'utilisateur peut voir Zavatsoa, montrer tout
  if (canViewZavatsoa(user)) {
    return transactions;
  }
  
  // Sinon, filtrer les transactions Zavatsoa
  return transactions.filter(transaction => {
    // Vérifier TOUS les champs possibles pour Zavatsoa
    const isZavatsoa = 
      transaction.category === 'Zavatsoa' || 
      transaction.category === 'zavatsoa' ||
      transaction.type === 'Zavatsoa' ||
      transaction.type === 'zavatsoa' ||
      (transaction.libelle && transaction.libelle.toLowerCase().includes('zavatsoa'));
    
    // Si c'est Zavatsoa, ne pas afficher
    if (isZavatsoa) {
      return false;
    }
    
    // Sinon, afficher
    return true;
  });
};

/**
 * Vérifie si un rôle a accès à un module selon les permissions dynamiques
 * @param {Object} user - L'utilisateur actuel
 * @param {string} moduleId - ID du module à vérifier
 * @returns {boolean}
 */
export const hasModuleAccess = (user, moduleId) => {
  if (!user) return false;
  
  // Admin a toujours accès à tout
  if (user.role === 'admin') return true;
  
  // 🆕 Essayer d'abord de charger depuis le cache mémoire (plus rapide)
  if (window.__modulePermissionsCache) {
    const cachedPermissions = window.__modulePermissionsCache;
    if (cachedPermissions[user.role] && cachedPermissions[user.role].hasOwnProperty(moduleId)) {
      return cachedPermissions[user.role][moduleId];
    }
  }
  
  // Fallback sur localStorage (pour la compatibilité)
  const modulePermissions = storage.get('modulePermissions') || {};
  
  // Si des permissions personnalisées existent pour ce rôle et ce module
  if (modulePermissions[user.role] && modulePermissions[user.role].hasOwnProperty(moduleId)) {
    // IMPORTANT: Les permissions personnalisées ont la priorité absolue
    return modulePermissions[user.role][moduleId];
  }
  
  // Sinon, utiliser les permissions par défaut de constants.js
  const role = ROLES[user.role];
  if (!role) return false;
  
  // Mapper les modules aux permissions
  const modulePermissionMap = {
    'dashboard': null, // Toujours accessible
    'programmes': 'view_programmes',
    'fihirana': 'view_fihirana',
    'birthdays': null, // Toujours accessible
    'journal': 'view_programmes',
    'transactions': 'view_finances',
    'members': 'view_members',
    'reports': 'export_all',
    'settings': null // Toujours accessible
  };
  
  const requiredPermission = modulePermissionMap[moduleId];
  
  // Si pas de permission requise, accessible par tous
  if (!requiredPermission) return true;
  
  // Vérifier si l'utilisateur a la permission
  return role.permissions?.includes(requiredPermission) || false;
};

// ========================================
// 2. FORMATAGE DES DONNÉES
// ========================================

/**
 * Formate un montant en euros
 * @param {number} amount - Montant à formater
 * @returns {string}
 */
export const formatCurrency = (amount) => {
  // Valider que le montant est un nombre valide
  const validAmount = parseAmount(amount);
  const formatted = new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(validAmount);
  
  // Remplacer TOUS les types d'espaces problématiques pour PDF
  return formatted
    .replace(/\u00A0/g, ' ')  // Espace insécable (non-breaking space)
    .replace(/\u202F/g, ' ')  // Espace fine insécable (narrow no-break space)
    .replace(/\u2009/g, ' ')  // Espace fine (thin space)
    .replace(/[\s\xA0\u2009\u200A\u202F]+/g, ' '); // Normaliser tous les espaces
};

/**
 * Parse un montant de manière sécurisée pour éviter NaN
 * @param {any} value - Valeur à parser
 * @returns {number} - Montant valide ou 0
 */
export const parseAmount = (value) => {
  // Si déjà un nombre valide
  if (typeof value === 'number' && !isNaN(value) && isFinite(value)) {
    return value;
  }
  
  // Si c'est une chaîne, essayer de la parser
  if (typeof value === 'string') {
    // Nettoyer la chaîne (enlever espaces, symboles monétaires, etc.)
    const cleaned = value.replace(/[^\d.,-]/g, '').replace(',', '.');
    const parsed = parseFloat(cleaned);
    return (!isNaN(parsed) && isFinite(parsed)) ? parsed : 0;
  }
  
  // Pour tout autre cas, retourner 0
  return 0;
};

/**
 * Formate une date en français
 * @param {string|Date} date - Date à formater
 * @param {boolean} long - Format long ou court
 * @returns {string}
 */
export const formatDate = (date, long = false) => {
  if (!date) return '-';
  
  const dateObj = new Date(date);
  
  // Vérifier si la date est valide
  if (isNaN(dateObj.getTime())) {
    return '-';
  }
  
  const options = long 
    ? { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }
    : { year: 'numeric', month: '2-digit', day: '2-digit' };
  
  return dateObj.toLocaleDateString('fr-FR', options);
};

/**
 * Vérifie si une date est valide
 * @param {any} date - Date à vérifier
 * @returns {boolean}
 */
export const isValidDate = (date) => {
  if (!date) return false;
  const dateObj = new Date(date);
  return !isNaN(dateObj.getTime());
};

/**
 * Obtient les initiales d'un nom
 * @param {string} name - Nom complet
 * @returns {string}
 */
export const getInitials = (name) => {
  if (!name) return '?';
  return name
    .split(' ')
    .map(word => word[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
};

// ========================================
// 3. CALCULS FINANCIERS
// ========================================

/**
 * Calcule le total par catégorie avec filtrage Zavatsoa
 * @param {Array} transactions - Liste des transactions
 * @param {Object} user - Utilisateur actuel (pour filtrage)
 * @returns {Object}
 */
export const calculateCategoryTotals = (transactions, user = null) => {
  // Filtrer selon permissions si user fourni
  const visibleTransactions = user 
    ? filterTransactionsByPermission(transactions, user)
    : transactions;

  const totals = {};
  
  visibleTransactions.forEach(transaction => {
    const category = transaction.category || 'Non catégorisé';
    const amount = parseFloat(transaction.amount) || 0;
    
    if (!totals[category]) {
      totals[category] = 0;
    }
    
    totals[category] += Math.abs(amount);
  });
  
  return totals;
};

/**
 * Calcule le solde total avec filtrage Zavatsoa
 * @param {Array} revenus - Liste des revenus
 * @param {Array} depenses - Liste des dépenses
 * @param {Object} user - Utilisateur actuel (pour filtrage)
 * @returns {number}
 */
export const calculateBalance = (revenus, depenses, user = null) => {
  // Filtrer selon permissions
  const visibleRevenus = user 
    ? filterTransactionsByPermission(revenus, user)
    : revenus;
  
  const visibleDepenses = user
    ? filterTransactionsByPermission(depenses, user)
    : depenses;

  const totalRevenus = visibleRevenus.reduce((sum, r) => 
    sum + (parseFloat(r.amount) || 0), 0);
  
  const totalDepenses = visibleDepenses.reduce((sum, d) => 
    sum + Math.abs(parseFloat(d.amount) || 0), 0);
  
  return totalRevenus - totalDepenses;
};

/**
 * Calcule les statistiques financières mensuelles avec filtrage Zavatsoa
 * @param {Array} revenus - Liste des revenus
 * @param {Array} depenses - Liste des dépenses
 * @param {Object} user - Utilisateur actuel (pour filtrage)
 * @returns {Object}
 */
export const calculateMonthlyStats = (revenus, depenses, user = null) => {
  const now = new Date();
  const currentMonth = now.getMonth();
  const currentYear = now.getFullYear();
  
  // Filtrer selon permissions
  const visibleRevenus = user 
    ? filterTransactionsByPermission(revenus, user)
    : revenus;
  
  const visibleDepenses = user
    ? filterTransactionsByPermission(depenses, user)
    : depenses;

  // Filtrer par mois actuel
  const monthlyRevenus = visibleRevenus.filter(r => {
    const date = new Date(r.date);
    return date.getMonth() === currentMonth && date.getFullYear() === currentYear;
  });

  const monthlyDepenses = visibleDepenses.filter(d => {
    const date = new Date(d.date);
    return date.getMonth() === currentMonth && date.getFullYear() === currentYear;
  });

  const totalRevenus = monthlyRevenus.reduce((sum, r) => 
    sum + (parseFloat(r.amount) || 0), 0);
  
  const totalDepenses = monthlyDepenses.reduce((sum, d) => 
    sum + Math.abs(parseFloat(d.amount) || 0), 0);

  return {
    revenus: totalRevenus,
    depenses: totalDepenses,
    solde: totalRevenus - totalDepenses,
    countRevenus: monthlyRevenus.length,
    countDepenses: monthlyDepenses.length
  };
};

// ========================================
// 4. VALIDATION
// ========================================

/**
 * Valide un email
 * @param {string} email - Email à valider
 * @returns {boolean}
 */
export const isValidEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

/**
 * Valide un numéro de téléphone
 * @param {string} phone - Téléphone à valider
 * @returns {boolean}
 */
export const isValidPhone = (phone) => {
  const phoneRegex = /^[\d\s\-\+\(\)]+$/;
  return phone && phone.length >= 10 && phoneRegex.test(phone);
};

/**
 * Valide un montant
 * @param {number|string} amount - Montant à valider
 * @returns {boolean}
 */
export const isValidAmount = (amount) => {
  const num = parseFloat(amount);
  return !isNaN(num) && num > 0;
};

// ========================================
// 5. UTILITAIRES DIVERS
// ========================================

/**
 * Génère un ID unique
 * @returns {string}
 */
export const generateId = () => {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

/**
 * Trie un tableau par date
 * @param {Array} items - Tableau à trier
 * @param {string} dateField - Nom du champ date
 * @param {boolean} ascending - Ordre ascendant ou descendant
 * @returns {Array}
 */
export const sortByDate = (items, dateField = 'date', ascending = false) => {
  return [...items].sort((a, b) => {
    const dateA = new Date(a[dateField]);
    const dateB = new Date(b[dateField]);
    return ascending ? dateA - dateB : dateB - dateA;
  });
};

/**
 * Filtre les éléments par recherche
 * @param {Array} items - Tableau à filtrer
 * @param {string} searchTerm - Terme de recherche
 * @param {Array} fields - Champs à rechercher
 * @returns {Array}
 */
export const filterBySearch = (items, searchTerm, fields = []) => {
  if (!searchTerm) return items;
  
  const term = searchTerm.toLowerCase();
  
  return items.filter(item => {
    return fields.some(field => {
      const value = item[field];
      if (!value) return false;
      return value.toString().toLowerCase().includes(term);
    });
  });
};

/**
 * Groupe les éléments par champ
 * @param {Array} items - Tableau à grouper
 * @param {string} field - Champ de groupement
 * @returns {Object}
 */
export const groupBy = (items, field) => {
  return items.reduce((groups, item) => {
    const key = item[field] || 'Non défini';
    if (!groups[key]) {
      groups[key] = [];
    }
    groups[key].push(item);
    return groups;
  }, {});
};

/**
 * Exporte des données en CSV
 * @param {Array} data - Données à exporter
 * @param {string} filename - Nom du fichier
 * @param {Object} user - Utilisateur actuel (pour filtrage Zavatsoa)
 */
export const exportToCSV = (data, filename, user = null) => {
  if (!data || data.length === 0) return;
  
  // Filtrer les données si ce sont des transactions
  const isTransaction = data[0]?.category !== undefined;
  const exportData = (isTransaction && user) 
    ? filterTransactionsByPermission(data, user)
    : data;

  // Obtenir les en-têtes
  const headers = Object.keys(exportData[0]);
  
  // Créer le contenu CSV
  const csvContent = [
    headers.join(','),
    ...exportData.map(row => 
      headers.map(header => {
        const value = row[header];
        // Échapper les guillemets et virgules
        return `"${String(value).replace(/"/g, '""')}"`;
      }).join(',')
    )
  ].join('\n');
  
  // Télécharger
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  
  link.setAttribute('href', url);
  link.setAttribute('download', `${filename}.csv`);
  link.style.visibility = 'hidden';
  
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

/**
 * Génère une référence unique pour une transaction
 * @returns {string}
 */
/**
 * Génère une référence unique pour une transaction
 * Format: [D|R]-yymmdd-xxxx
 * D = Dépense, R = Revenu
 * yymmdd = date au format année-mois-jour (2 chiffres chacun)
 * xxxx = numéro séquentiel (4 chiffres)
 * @param {string} type - 'D' pour dépense, 'R' pour revenu
 * @param {number} count - Nombre de transactions existantes du même type
 * @returns {string}
 */
/**
 * Génère une référence pour une transaction basée sur sa date
 * Format: {type}-{AAMMJJ}-{compteur}
 * @param {string} type - Type de transaction ('R' pour revenu, 'D' pour dépense)
 * @param {string|Date} transactionDate - Date de la transaction
 * @param {number} count - Compteur pour cette date spécifique (optionnel)
 * @returns {string} - Référence formatée (ex: D-251015-0001)
 */
export const generateReference = (type = 'D', transactionDate = null, count = 0) => {
  // Utiliser la date de transaction si fournie, sinon la date actuelle
  const date = transactionDate ? new Date(transactionDate) : new Date();
  
  const year = date.getFullYear().toString().slice(-2);
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');
  const sequence = (count + 1).toString().padStart(4, '0');
  
  const prefix = type === 'R' ? 'R' : 'D';
  return `${prefix}-${year}${month}${day}-${sequence}`;
};

/**
 * Obtient une couleur d'avatar basée sur le nom
 * @param {string} name - Nom de la personne
 * @returns {string}
 */
export const getAvatarColor = (name) => {
  const colors = [
    'bg-red-500', 'bg-blue-500', 'bg-green-500', 'bg-yellow-500',
    'bg-purple-500', 'bg-pink-500', 'bg-indigo-500', 'bg-orange-500'
  ];
  
  if (!name) return colors[0];
  
  // Utiliser le code ASCII du premier caractère pour choisir une couleur
  const charCode = name.charCodeAt(0);
  return colors[charCode % colors.length];
};

/**
 * Stockage local sécurisé
 */
export const storage = {
  set: (key, value) => {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch (error) {
      logger.error('Error saving to localStorage:', error);
    }
  },
  get: (key) => {
    try {
      const item = localStorage.getItem(key);
      return item ? JSON.parse(item) : null;
    } catch (error) {
      logger.error('Error reading from localStorage:', error);
      return null;
    }
  },
  remove: (key) => {
    try {
      localStorage.removeItem(key);
    } catch (error) {
      logger.error('Error removing from localStorage:', error);
    }
  },
  clear: () => {
    try {
      localStorage.clear();
    } catch (error) {
      logger.error('Error clearing localStorage:', error);
    }
  }
};

/**
 * Exporte les membres en PDF (via jsPDF)
 * Note: Nécessite jspdf et jspdf-autotable
 * @param {Array} membres - Liste des membres
 * @param {string} filename - Nom du fichier
 * @param {Object} options - Options d'export (colonnes à inclure)
 */
export const exportMembersToPDF = (membres, filename = 'membres', options = {}) => {
  try {
    // Options par défaut
    const defaultOptions = {
      name: true,
      email: true,
      phone: true,
      address: true,
      dateNaissance: true,
      status: true,
      fonction: true,
      originCity: true,
      mpandray: true
    };
    
    const selectedOptions = { ...defaultOptions, ...options };
    
    // Utiliser l'import statique depuis le haut du fichier
    const doc = new jsPDF('landscape'); // Mode paysage pour plus de colonnes
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    
    // Fonction pour ajouter le filigrane sur chaque page
    const addWatermark = () => {
      doc.saveGraphicsState();
      doc.setTextColor(200, 200, 200);
      doc.setFontSize(50);
      doc.setFont('helvetica', 'bold');
      
      // Rotation et positionnement du texte
      const text = 'DOCUMENT CONFIDENTIEL';
      const textWidth = doc.getTextWidth(text);
      
      doc.text(
        text,
        pageWidth / 2,
        pageHeight / 2,
        {
          align: 'center',
          angle: 45,
          renderingMode: 'stroke',
          lineWidth: 0.3
        }
      );
      
      doc.restoreGraphicsState();
    };
    
    // Ajouter le filigrane sur la première page
    addWatermark();
    
    // Titre
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(0, 0, 0);
    doc.text('Liste des Membres - VOMM FJKM Hannover', pageWidth / 2, 15, { align: 'center' });
    
    // Date d'export
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(`Exporté le: ${new Date().toLocaleDateString('fr-FR')}`, 14, 25);
    doc.text(`Nombre de membres: ${membres.length}`, pageWidth - 14, 25, { align: 'right' });

    // Construire les en-têtes et les données selon les options sélectionnées
    const headers = [];
    const columnMap = [];
    
    if (selectedOptions.name) {
      headers.push('Nom');
      columnMap.push('name');
    }
    if (selectedOptions.email) {
      headers.push('Email');
      columnMap.push('email');
    }
    if (selectedOptions.phone) {
      headers.push('Téléphone');
      columnMap.push('phone');
    }
    if (selectedOptions.address) {
      headers.push('Adresse');
      columnMap.push('address');
    }
    if (selectedOptions.dateNaissance) {
      headers.push('Date de naissance');
      columnMap.push('dateNaissance');
    }
    if (selectedOptions.originCity) {
      headers.push("Ville d'origine");
      columnMap.push('originCity');
    }
    if (selectedOptions.fonction) {
      headers.push('Fonction');
      columnMap.push('fonction');
    }
    if (selectedOptions.status) {
      headers.push('Statut');
      columnMap.push('status');
    }
    if (selectedOptions.mpandray) {
      headers.push('Mpandray');
      columnMap.push('mpandray');
    }

    // Préparer les données
    const tableData = membres.map(m => 
      columnMap.map(field => {
        if (field === 'dateNaissance' && m[field]) {
          return formatDate(m[field]);
        }
        return m[field] || '-';
      })
    );

    // Créer le tableau avec autoTable
    autoTable(doc, {
      head: [headers],
      body: tableData,
      startY: 30,
      styles: { 
        fontSize: 8,
        cellPadding: 2
      },
      headStyles: { 
        fillColor: [45, 59, 135],
        textColor: 255,
        fontStyle: 'bold'
      },
      alternateRowStyles: {
        fillColor: [245, 247, 250]
      },
      didDrawPage: function(data) {
        // Ajouter le filigrane sur chaque nouvelle page
        if (data.pageNumber > 1) {
          addWatermark();
        }
        
        // Ajouter le numéro de page
        doc.setFontSize(8);
        doc.setTextColor(128);
        doc.text(
          `Page ${data.pageNumber}`,
          pageWidth / 2,
          pageHeight - 10,
          { align: 'center' }
        );
      }
    });

    // Sauvegarder
    doc.save(`${filename}.pdf`);
    logger.info(`PDF exported successfully: ${filename}.pdf`);
  } catch (error) {
    logger.error('Error exporting to PDF:', error);
    // Fallback vers CSV
    exportToCSV(membres, filename);
  }
};

/**
 * Calcule l'âge à partir d'une date de naissance
 * @param {string|Date} dateNaissance - Date de naissance
 * @returns {number}
 */
export const calculateAge = (birthDate) => {
  if (!birthDate) return null;

  try {
    const today = new Date();
    const birth = birthDate.toDate ? birthDate.toDate() : new Date(birthDate);

    // Vérifier que la date est valide
    if (isNaN(birth.getTime())) return null;

    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();

    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }

    return age;
  } catch (error) {
    return null;
  }
};

// ========================================
// CALCULS FINANCIERS
// ========================================

export const getFinancialSummary = (transactions) => {
  if (!transactions || !Array.isArray(transactions)) {
    transactions = [];
  }

  const summary = {
    totalRecettes: 0,
    totalDepenses: 0,
    balance: 0
  };

  transactions.forEach(tx => {
    if (!tx || !tx.type || !tx.amount) return;

    const amount = parseFloat(tx.amount);
    if (isNaN(amount)) return;

    if (tx.type === 'income' || tx.type === 'recette') {
      summary.totalRecettes += amount;
    } else if (tx.type === 'expense' || tx.type === 'depense') {
      summary.totalDepenses += amount;
    }
  });

  // Arrondir à 2 décimales
  summary.totalRecettes = Math.round(summary.totalRecettes * 100) / 100;
  summary.totalDepenses = Math.round(summary.totalDepenses * 100) / 100;
  summary.balance = Math.round((summary.totalRecettes - summary.totalDepenses) * 100) / 100;

  return summary;
};


/**
 * Vérifie si un anniversaire est proche (dans les X jours)
 * @param {string|Date} dateNaissance - Date de naissance
 * @param {number} days - Nombre de jours
 * @returns {boolean}
 */
export const isUpcomingBirthday = (dateNaissance, days = 30) => {
  if (!dateNaissance) return false;
  
  const today = new Date();
  const birthDate = new Date(dateNaissance);
  const currentYear = today.getFullYear();
  
  // Créer la date d'anniversaire pour cette année
  const thisBirthday = new Date(currentYear, birthDate.getMonth(), birthDate.getDate());
  
  // Si l'anniversaire est passé cette année, vérifier l'année prochaine
  if (thisBirthday < today) {
    thisBirthday.setFullYear(currentYear + 1);
  }
  
  // Calculer la différence en jours
  const diffTime = thisBirthday - today;
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  
  return diffDays >= 0 && diffDays <= days;
};

/**
 * Exporte un programme en PDF professionnel
 * @param {Object} programme - Programme à exporter
 * @param {string} logoBase64 - Logo en base64
 * @param {Array} fihirana - Liste des fihirana (cantiques) pour récupérer les titres
 */
export const exportProgrammeToPDF = (programme, logoBase64 = null, fihirana = []) => {
  try {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 20;
    const contentWidth = pageWidth - (margin * 2);
    let yPos = margin;

    // Ajouter le logo si disponible
    if (logoBase64) {
      try {
        const logoSize = 25;
        const logoX = (pageWidth - logoSize) / 2;
        doc.addImage(logoBase64, 'JPEG', logoX, yPos, logoSize, logoSize);
        yPos += logoSize + 10;
      } catch (e) {
        logger.warn('Erreur ajout logo:', e);
      }
    }

    // En-tête avec nom de l'église
    doc.setFontSize(20);
    doc.setFont('helvetica', 'bold');
    doc.text('VOMM FJKM HANNOVER', pageWidth / 2, yPos, { align: 'center' });
    yPos += 8;

    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    doc.text('Fandaharana', pageWidth / 2, yPos, { align: 'center' });
    yPos += 10;

    // Ligne de séparation
    doc.setLineWidth(0.5);
    doc.line(margin, yPos, pageWidth - margin, yPos);
    yPos += 15;

    // Titre du programme
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    const titleLines = doc.splitTextToSize(programme.title || 'Programme', contentWidth);
    titleLines.forEach(line => {
      doc.text(line, pageWidth / 2, yPos, { align: 'center' });
      yPos += 8;
    });
    yPos += 5;

    // Date et heure
    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    const dateText = `${formatDate(programme.date)} à ${programme.time || ''}`;
    doc.text(dateText, pageWidth / 2, yPos, { align: 'center' });
    yPos += 10;

    // Ligne de séparation
    doc.setLineWidth(0.3);
    doc.line(margin, yPos, pageWidth - margin, yPos);
    yPos += 15;

    // Vérifier si on a de la place pour les items
    if (!programme.items || programme.items.length === 0) {
      doc.setFontSize(11);
      doc.text('Aucun élément dans ce programme', pageWidth / 2, yPos, { align: 'center' });
    } else {
      // Items du programme
      doc.setFontSize(11);
      
      programme.items.forEach((item, index) => {
        // Vérifier si on a besoin d'une nouvelle page
        if (yPos > pageHeight - 40) {
          doc.addPage();
          yPos = margin;
        }

        // Numéro et type
        doc.setFont('helvetica', 'bold');
        const itemNumber = `${index + 1}.`;
        doc.text(itemNumber, margin, yPos);
        
        // Type d'élément avec première lettre en majuscule
        const itemType = item.type || 'Élément';
        const formattedType = itemType.charAt(0).toUpperCase() + itemType.slice(1).toLowerCase();
        doc.text(formattedType, margin + 10, yPos);
        yPos += 7;

        // Titre de l'item
        // Pour les Hira, récupérer le titre ET le numéro depuis fihirana si fihiranaId existe
        doc.setFont('helvetica', 'normal');
        let itemTitle = item.title || '';
        let cantiqueNumber = null;
        
        // Si c'est un Hira et qu'il a un fihiranaId, chercher le titre et numéro du cantique
        if ((item.type === 'hira' || item.type === 'hira_stk') && item.fihiranaId && !itemTitle) {
          const cantique = fihirana.find(f => f.id === item.fihiranaId);
          if (cantique) {
            itemTitle = cantique.title;
            cantiqueNumber = cantique.number;
          }
        }
        
        if (itemTitle) {
          // Si on a le numéro du cantique, l'afficher avec le titre
          const fullTitle = cantiqueNumber ? `n°${cantiqueNumber} - ${itemTitle}` : itemTitle;
          const titleLines = doc.splitTextToSize(fullTitle, contentWidth - 15);
          titleLines.forEach(line => {
            if (yPos > pageHeight - 40) {
              doc.addPage();
              yPos = margin;
            }
            doc.text(line, margin + 15, yPos);
            yPos += 6;
          });
        }

        // Détails (responsable, notes, fihirana, andininy)
        doc.setFontSize(10);
        doc.setFont('helvetica', 'italic');

        if (item.responsible) {
          doc.text(`Responsable: ${item.responsible}`, margin + 15, yPos);
          yPos += 5;
        }

        if (item.fihiranaNumber) {
          doc.text(`Fihirana: ${item.fihiranaNumber}`, margin + 15, yPos);
          yPos += 5;
        }

        // Afficher Andininy (versets) pour les éléments Hira
        if ((item.type === 'hira' || item.type === 'hira_stk') && item.andininy) {
          doc.text(`Andininy: ${item.andininy}`, margin + 15, yPos);
          yPos += 5;
        }

        if (item.notes) {
          const notesLines = doc.splitTextToSize(`Note: ${item.notes}`, contentWidth - 15);
          notesLines.forEach(line => {
            if (yPos > pageHeight - 40) {
              doc.addPage();
              yPos = margin;
            }
            doc.text(line, margin + 15, yPos);
            yPos += 5;
          });
        }

        // Durée
        if (item.duration && !item.durationIndeterminate) {
          doc.setFont('helvetica', 'normal');
          doc.text(`(${item.duration} min)`, pageWidth - margin - 20, yPos - 10);
        }

        yPos += 8;

        // Ligne de séparation légère entre items
        if (index < programme.items.length - 1) {
          doc.setDrawColor(200, 200, 200);
          doc.setLineWidth(0.1);
          doc.line(margin + 10, yPos, pageWidth - margin - 10, yPos);
          yPos += 8;
        }
      });
    }

    // Pied de page
    yPos = pageHeight - 25;
    doc.setDrawColor(0, 0, 0);
    doc.setLineWidth(0.3);
    doc.line(margin, yPos, pageWidth - margin, yPos);
    yPos += 8;

    doc.setFontSize(9);
    doc.setFont('helvetica', 'italic');
    doc.text('Soli Deo gloria', pageWidth / 2, yPos, { align: 'center' });
    yPos += 5;
    doc.setFontSize(8);
    doc.text(`Édité le ${new Date().toLocaleDateString('fr-FR')}`, pageWidth / 2, yPos, { align: 'center' });

    // Sauvegarder
    const filename = `Programme_${programme.title?.replace(/[^a-z0-9]/gi, '_')}_${formatDate(programme.date)}.pdf`;
    doc.save(filename);
    logger.info(`Programme PDF exporté: ${filename}`);
  } catch (error) {
    logger.error('Erreur export programme PDF:', error);
    alert('Erreur lors de l\'export du programme en PDF');
  }
};

// ========================================
// EXPORT PAR DÉFAUT
// ========================================

export default {
  // Permissions
  hasPermission,
  canViewZavatsoa,
  filterTransactionsByPermission,
  hasModuleAccess,
  
  // Formatage
  formatCurrency,
  formatDate,
  getInitials,
  getAvatarColor,
  
  // Calculs
  calculateCategoryTotals,
  calculateBalance,
  calculateMonthlyStats,
  
  // Validation
  isValidEmail,
  isValidPhone,
  isValidAmount,
  
  // Utilitaires
  generateId,
  generateReference,
  sortByDate,
  filterBySearch,
  groupBy,
  exportToCSV,
  exportMembersToPDF,
  exportProgrammeToPDF,
  calculateAge,
  isUpcomingBirthday,
  storage
};
/**
 * Formatte un montant en euros SPÉCIFIQUEMENT pour les PDF
 * Sans aucun espace problématique
 * @param {number} amount - Montant à formater
 * @returns {string} - Montant formaté (ex: "1 372,55 €")
 */
export const formatCurrencyForPDF = (amount) => {
  const validAmount = parseAmount(amount);
  
  // Formater manuellement pour éviter tout problème d'espace
  const isNegative = validAmount < 0;
  const absAmount = Math.abs(validAmount);
  
  // Séparer partie entière et décimale
  const parts = absAmount.toFixed(2).split('.');
  let integerPart = parts[0];
  const decimalPart = parts[1];
  
  // Ajouter séparateur de milliers (espace normal)
  integerPart = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  
  // Construire le résultat
  const result = `${isNegative ? '-' : ''}${integerPart},${decimalPart} €`;
  
  return result;
};